import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";

const API_URL = "https://sunrisemeditechsystems.com/api";

export default function Checkout() {
  const navigate = useNavigate();
  // Add this with other state declarations at the top
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [savedAddress, setSavedAddress] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveAddress, setSaveAddress] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false); // Add this line
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    companyAddress: "",
    country: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    notes: ""
  });

  const calculateTotal = useCallback((items) => {
    const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setCartTotal(total);
  }, []);

  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSaveAddress = async () => {
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        Swal.fire("Error", "Please login to save address", "error");
        return;
      }
  
      // Validate required fields
      const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'country', 'address1', 'city', 'state', 'zip'];
      const missingFields = requiredFields.filter(field => !form[field]);
      
      if (missingFields.length > 0) {
        Swal.fire("Error", `Please fill in all required fields: ${missingFields.join(', ')}`, "error");
        return;
      }
  
      const response = await axios.post(
        `${API_URL}/orders/save-address`,
        { 
          address: {
            ...form,
            isSaved: true
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      if (response.data.success) {
        setSavedAddress(response.data.shippingAddress);
        setEditMode(false);
        Swal.fire("Success", "Address saved successfully!", "success");
      }
    } catch (error) {
      console.error("Failed to save address:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to save address", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      Swal.fire("Error", "Your cart is empty.", "error");
      return;
    }

    if (saveAddress && !savedAddress) {
      await handleSaveAddress();
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("userToken");
      const orderData = {
        items: cartItems.map((item) => ({
          product: item.productId,
          name: item.name,
          image: item.image,
          price: item.price,
          quantity: item.quantity,
          buyOrRent: item.type || "buy",
          rentalStartDate: item.rentalStartDate,
          rentalEndDate: item.rentalEndDate,
        })),
        shippingAddress: form,
        source: 'sunrise-website'
      };

      const res = await axios.post(
        `${API_URL}/orders`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data && res.data.order) {
        const userId = localStorage.getItem("currentUserId");
        localStorage.removeItem(`cart_${userId}`);
        setOrderPlaced(true); // Add this line
        
        Swal.fire({
          title: "Success!",
          text: "Your order has been placed successfully!",
          icon: "success",
          confirmButtonText: "View Orders"
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/account");
          }
        });
      }
    } catch (error) {
      console.error("Order placement error:", error);
      Swal.fire("Error", "Failed to place order. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) {
      // navigate("/login", { state: { from: "/checkout" } });
       Swal.fire("Login Required", "Please login to proceed to checkout.", "info")
      return;
    }

    const userId = localStorage.getItem("currentUserId");
    const cartKey = `cart_${userId}`;
    const items = JSON.parse(localStorage.getItem(cartKey) || "[]");
    if (items.length === 0) {
      navigate("/cart");
      return;
    }
    setCartItems(items);
    calculateTotal(items);

    // Fetch saved address
    const fetchSavedAddress = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/order/my-latest-address`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data && data.shipping_address) {
          setSavedAddress(data.shipping_address);
          setForm(data.shipping_address);
          setEditMode(false);
        } else {
          setEditMode(true);
        }
      } catch (error) {
        console.error("Failed to fetch saved address:", error);
        setEditMode(true);
      }
    };

    fetchSavedAddress();
  }, [navigate, calculateTotal]);

  const AddressDisplay = ({ address, onEdit }) => (
    <div className="ltn__checkout-single-content-info">
      <div className="row">
        <div className="col-12">
          <div className="border p-3 rounded mb-4">
            <h5>Shipping Address</h5>
            <p className="mb-1"><strong>Name:</strong> {address.firstName} {address.lastName}</p>
            <p className="mb-1"><strong>Email:</strong> {address.email}</p>
            <p className="mb-1"><strong>Phone:</strong> {address.phone}</p>
            <p className="mb-1"><strong>Address:</strong> {address.address1}</p>
            {address.address2 && <p className="mb-1">{address.address2}</p>}
            <p className="mb-1">{address.city}, {address.state} {address.zip}</p>
            <p className="mb-1">{address.country}</p>
            {address.company && (
              <>
                <p className="mb-1"><strong>Company:</strong> {address.company}</p>
                <p className="mb-1">{address.companyAddress}</p>
              </>
            )}
            <div className="mt-3">
              <button 
                className="btn theme-btn-2 btn-effect-2"
                onClick={onEdit}
              >
                Edit Address
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (orderPlaced) {
    return (
      <div className="container" style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <h2 className="mb-3">Thank you for your order!</h2>
          <p>Your order has been placed successfully. You can view your order details in your <a href="/account">account</a>.</p>
          <button className="btn theme-btn-1 btn-effect-1 mt-3" onClick={() => navigate("/")}>Go to Home</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* BREADCRUMB AREA START */}
      <div
        className="ltn__breadcrumb-area text-left bg-overlay-white-30 bg-image "
        data-bs-bg="img/bg/sunrise-medical.jpg"
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="ltn__breadcrumb-inner">
                <h1 className="page-title">Checkout</h1>
                <div className="ltn__breadcrumb-list">
                  <ul>
                    <li>
                      <a href="/">
                        <span className="ltn__secondary-color">
                          <i className="fas fa-home" />
                        </span>{" "}
                        Home
                      </a>
                    </li>
                    <li>Checkout</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* BREADCRUMB AREA END */}
      {/* CHECKOUT AREA START */}
      <div className="ltn__checkout-area mb-105">
        <div className="container">
          <div className="row">
            <div className="col-lg-7">
              <div className="ltn__checkout-inner">
                <div className="ltn__checkout-single-content mt-50">
                   <h4 className="title-2">Billing Details</h4>
                  {savedAddress && !editMode ? (
                    <AddressDisplay address={savedAddress} onEdit={() => setEditMode(true)} />
                  ) : (
                    <div className="ltn__checkout-single-content-info">
                      <form id="checkout-form" onSubmit={handleSubmit}>
                        <h6>Personal Information</h6>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="input-item input-item-name ltn__custom-icon">
                              <input type="text" name="firstName" className="fullInput" placeholder="First name" value={form.firstName || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-item input-item-name ltn__custom-icon">
                              <input type="text" name="lastName" className="fullInput" placeholder="Last name" value={form.lastName || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-item input-item-email ltn__custom-icon">
                              <input type="email" name="email" className="fullInput" placeholder="email address" value={form.email || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-item input-item-phone ltn__custom-icon">
                              <input type="text" name="phone" className="fullInput" maxLength="10" placeholder="phone number" value={form.phone || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-item input-item-website ltn__custom-icon">
                              <input type="text" name="company" className="fullInput" placeholder="Company name (optional)" value={form.company || ''} onChange={handleInput} />
                            </div>
                          </div>
                            <div className="col-md-6">
                              <div className="input-item input-item-website ltn__custom-icon">
                                  <input type="text" name="companyAddress" className="fullInput" placeholder="Company address (optional)" value={form.companyAddress || ''} onChange={handleInput} />
                              </div>
                            </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-4 col-md-6">
                            <h6>Country</h6>
                            <div className="input-item">
                              <select className="nice-select fullInput" name="country" value={form.country || ''} onChange={handleInput} required >
                                <option value="">Select Country</option>
                                <option value="India">India</option>
                                <option value="Australia">Australia</option>
                                <option value="Canada">Canada</option>
                                <option value="China">China</option>
                                <option value="Morocco">Morocco</option>
                                <option value="Saudi Arabia">Saudi Arabia</option>
                                <option value="United Kingdom (UK)">United Kingdom (UK)</option>
                                <option value="United States (US)">United States (US)</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-lg-12 col-md-12">
                            <h6>Address</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item">
                                  <input type="text" name="address1" className="fullInput" placeholder="House number and street name" value={form.address1 || ''} onChange={handleInput} required />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item">
                                  <input type="text" name="address2" className="fullInput" placeholder="Apartment, suite, unit etc. (optional)" value={form.address2 || ''} onChange={handleInput} />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-6">
                            <h6>Town / City</h6>
                            <div className="input-item">
                              <input type="text" name="city" className="fullInput" placeholder="City" value={form.city || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-6">
                            <h6>State </h6>
                            <div className="input-item">
                              <input type="text" name="state"  className="fullInput" placeholder="State" value={form.state || ''} onChange={handleInput} required />
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-6">
                            <h6>Zip</h6>
                            <div className="input-item">
                              <input type="text" name="zip"  className="fullInput" placeholder="Zip" value={form.zip || ''} onChange={handleInput} required />
                            </div>
                          </div>
                        </div>
                        <h6>Order notes (optional)</h6>
                        <div className="input-item input-item-textarea ltn__custom-icon">
                          <textarea name="notes" placeholder="Notes about your order, e.g. special notes for delivery." value={form.notes || ''} onChange={handleInput} />
                        </div>
                        
                        {/* Save Address Checkbox */}
                        <div className="input-item input-item-checkbox">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="saveAddressCheckbox"
                              checked={saveAddress}
                              onChange={(e) => setSaveAddress(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="saveAddressCheckbox">
                              Save this address for future orders
                            </label>
                          </div>
                        </div>
                        
                        {saveAddress && (
                          <div className="mt-3">
                            <button
                              type="button"
                              className="btn theme-btn-2 btn-effect-2"
                              onClick={handleSaveAddress}
                            >
                              Save Address
                            </button>
                          </div>
                        )}
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="shoping-cart-total mt-50">
                <h4 className="title-2">Cart Totals</h4>
                <table className="table">
                  <tbody>
                    {cartItems.length === 0 ? (
                      <tr><td colSpan="2">Your cart is empty.</td></tr>
                    ) : (
                      cartItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            {item.name} <strong>× {item.quantity}</strong>
                          </td>
                          <td>₹{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                    <tr>
                      <td><strong>Order Total</strong></td>
                      <td><strong>₹{cartTotal.toFixed(2)}</strong></td>
                    </tr>
                  </tbody>
                </table>
                <div className="ltn__checkout-payment-method mt-50">
                  <div className="ltn__payment-note mt-30 mb-30">
                    <p>Your personal data will be used to process your order, support your experience throughout this website, and for other purposes described in our privacy policy.</p>
                  </div>
                   <button
                      className="btn theme-btn-1 btn-effect-1 text-uppercase"
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting || cartItems.length === 0}
                    >
                      {isSubmitting ? "Placing Order..." : "Place order"}
                    </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CHECKOUT AREA END */}
    </>
  );
}